import type { CustomerItem } from "../mocks/customers";
import type { ApiCustomer } from "../lib/api";
import {
  createCustomerRequest,
  listCustomersRequest,
  updateCustomerRequest,
} from "../lib/api";

type CustomerUpdater =
  | CustomerItem[]
  | ((prev: CustomerItem[]) => CustomerItem[]);

type Listener = () => void;

let currentCustomers: CustomerItem[] = [];
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((listener) => listener());
}

function cloneCustomers(items: CustomerItem[]) {
  return items.map((item) => ({ ...item }));
}

function mapApiCustomer(item: ApiCustomer): CustomerItem {
  return {
    id: item.id,
    adSoyad: item.ad_soyad,
    telefon: item.telefon,
    kisaNot: item.kisa_not ?? undefined,
    toplamHisseSayisi: item.toplam_hisse_sayisi,
    toplamBorc: Number(item.toplam_borc),
    toplamOdenen: Number(item.toplam_odenen),
  };
}

function comparableCustomer(item: CustomerItem) {
  return {
    adSoyad: item.adSoyad,
    telefon: item.telefon,
    kisaNot: item.kisaNot ?? "",
    toplamHisseSayisi: item.toplamHisseSayisi,
    toplamBorc: item.toplamBorc,
    toplamOdenen: item.toplamOdenen,
  };
}

export function getCustomers(): CustomerItem[] {
  return currentCustomers;
}

export async function rehydrateCustomers() {
  try {
    currentCustomers = (await listCustomersRequest()).map(mapApiCustomer);
    emit();
  } catch {
    currentCustomers = [];
    emit();
  }
}

export async function setAllCustomers(updater: CustomerUpdater) {
  const prev = cloneCustomers(currentCustomers);
  const next = typeof updater === "function" ? updater(cloneCustomers(prev)) : updater;

  const prevMap = new Map(prev.map((item) => [item.id, item]));
  const nextMap = new Map(next.map((item) => [item.id, item]));

  for (const nextItem of next) {
    const prevItem = prevMap.get(nextItem.id);

    if (!prevItem) {
      await createCustomerRequest({
        ad_soyad: nextItem.adSoyad.trim(),
        telefon: nextItem.telefon,
        kisa_not: nextItem.kisaNot?.trim() || null,
      });
      continue;
    }

    const before = JSON.stringify(comparableCustomer(prevItem));
    const after = JSON.stringify(comparableCustomer(nextItem));

    if (before !== after) {
      await updateCustomerRequest(nextItem.id, {
        ad_soyad: nextItem.adSoyad.trim(),
        telefon: nextItem.telefon,
        kisa_not: nextItem.kisaNot?.trim() || null,
        toplam_hisse_sayisi: nextItem.toplamHisseSayisi,
        toplam_borc: nextItem.toplamBorc,
        toplam_odenen: nextItem.toplamOdenen,
      });
    }
  }

  const deletedIds = prev.filter((item) => !nextMap.has(item.id)).map((item) => item.id);
  if (deletedIds.length > 0) {
    console.warn("Customer delete API henüz bağlanmadı:", deletedIds);
  }

  await rehydrateCustomers();
}

export function resetCustomersStore() {
  currentCustomers = [];
  emit();
}

export function subscribeCustomers(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}